import { ImportDialog } from "@/components/ImportDialog";
import {
	type EntityFilterSchema,
	type TreeNode,
	useFilters,
} from "@/components/filters";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { type StatusBarItem, useWorkspacePage } from "@/components/workspace";
import { CatalogService } from "@/gen/omnios/v1/catalog_connect";
import type { Product } from "@/gen/omnios/v1/catalog_pb";
import { MediaService } from "@/gen/omnios/v1/media_connect";
import { useGridSelection } from "@/hooks/use-grid-selection";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";
import {
	GridSpinner,
	LoadingCellRenderer,
	agFilterModelToProto,
} from "@/lib/use-server-grid";
import { cn } from "@/lib/utils";

import { FilterGroup, FilterGroup_Logic } from "@/gen/omnios/v1/common_pb";
import type {
	ColDef,
	IDatasource,
	IGetRowsParams,
} from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	ChevronDown,
	ChevronRight,
	Folder,
	FolderOpen,
	FolderTree,
	Plus,
	Search,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/catalog/products/")({
	component: ProductsPage,
	validateSearch: (search: Record<string, unknown>) => ({
		// Filter the grid to products owned by a specific
		// supplier party. Empty/missing = no filter.
		owner_party_id:
			typeof search.owner_party_id === "string" && search.owner_party_id
				? search.owner_party_id
				: undefined,
	}),
});

const client = createClient(CatalogService, transport);
const mediaClient = createClient(MediaService, transport);

const PAGE_SIZE = 50;

function formatPrice(p: Product): string {
	const def = p.pricing.find((pr) => pr.isDefault) ?? p.pricing[0];
	if (!def) return "—";
	return `${(Number(def.valueCents) / 100).toFixed(2)} ${def.currency || "₽"}`;
}

function formatSupplierCost(p: Product): string {
	if (!p.supplierCostCents) return "—";
	return `${(Number(p.supplierCostCents) / 100).toFixed(2)} ${p.supplierCostCurrency || "₽"}`;
}

function sellPrice(p: Product) {
	// The dedicated sell price if set, otherwise the default price.
	return (
		p.pricing.find((pr) => pr.name === "SELL") ??
		p.pricing.find((pr) => pr.isDefault) ??
		p.pricing[0]
	);
}

function computeMarginPct(p: Product): number | null {
	const sell = sellPrice(p);
	const sellCents = sell ? Number(sell.valueCents) : 0;
	const costCents = Number(p.supplierCostCents);
	if (!sell || sellCents <= 0 || costCents <= 0) return null;
	if ((sell.currency || "") !== (p.supplierCostCurrency || "")) return null;
	// Margin (not markup): (sell − cost) / sell. Denominator is the client
	// price, per the sales team's definition.
	return Math.round(((sellCents - costCents) / sellCents) * 100);
}

function formatVendor(p: Product): string {
	const def = p.vendorCodes.find((v) => v.isDefault) ?? p.vendorCodes[0];
	return def?.code ?? "—";
}

function ProductsPage() {
	const { t } = useTranslation("products");
	useBreadcrumbs([{ label: t("breadcrumb_products") }]);

	const search = Route.useSearch();
	const ownerPartyId = search.owner_party_id;

	const qc = useQueryClient();
	const navigate = useNavigate();
	const gridRef = useRef<AgGridReact>(null);
	const { onGridReady, onStateChanged } = useGridLayout("products");
	const [dialogOpen, setDialogOpen] = useState(false);
	const [importDialogOpen, setImportDialogOpen] = useState(false);

	const refreshGrid = useCallback(() => {
		pageTokenMap.current.clear();
		gridRef.current?.api?.purgeInfiniteCache();
	}, []);

	const { data: categoriesResp } = useQuery({
		queryKey: ["categories", "*"],
		queryFn: () => client.listCategories({ parentId: "*" }),
		staleTime: 5 * 60_000,
	});
	const categories = categoriesResp?.categories ?? [];

	// Build a map from category ID to category for parent lookups
	const categoryById = useMemo(
		() => new Map(categories.map((c) => [c.id, c])),
		[categories],
	);

	// Helper to build readable path by traversing parents
	const buildReadablePath = useCallback(
		(catId: string): string => {
			const parts: string[] = [];
			let current = categoryById.get(catId);
			while (current) {
				parts.unshift(current.name || current.slug || current.id);
				current = current.parentId
					? categoryById.get(current.parentId)
					: undefined;
			}
			return parts.join(" / ");
		},
		[categoryById],
	);

	const catMap = useMemo(
		() =>
			new Map(
				categories.map((c) => [
					c.id,
					{
						name: c.name || c.slug || c.id,
						fullPath: buildReadablePath(c.id),
					},
				]),
			),
		[categories, buildReadablePath],
	);

	// Categories with readable paths for selectors
	const categoriesWithReadablePath = useMemo(
		() =>
			categories.map((c) => ({
				id: c.id,
				parentId: c.parentId,
				name: c.name || c.slug || c.id,
				readablePath: buildReadablePath(c.id),
			})),
		[categories, buildReadablePath],
	);

	// Tree nodes for category filter
	const categoryNodes: TreeNode[] = useMemo(
		() =>
			categories.map((c) => ({
				id: c.id,
				parentId: c.parentId,
				name: c.name || c.slug || c.id,
				path: c.path,
				readablePath: buildReadablePath(c.id),
			})),
		[categories, buildReadablePath],
	);

	// Filter schema for products
	const filterSchema: EntityFilterSchema = useMemo(
		() => ({
			entityType: "product",
			fields: [
				{
					id: "status",
					label: t("filter_status"),
					type: "select" as const,
					options: [
						{ value: "draft", label: t("filter_status_draft") },
						{ value: "active", label: t("filter_status_active") },
					],
				},
				{
					id: "category_id",
					label: t("filter_category"),
					type: "tree" as const,
					treeNodes: categoryNodes,
				},
				{
					id: "title",
					label: t("filter_title"),
					type: "text" as const,
				},
				{
					id: "quality_score",
					label: t("filter_quality"),
					type: "number" as const,
				},
			],
			quickFilters: [
				{
					id: "drafts",
					label: t("quick_drafts"),
					filters: [{ fieldId: "status", operator: "eq", value: "draft" }],
				},
				{
					id: "active",
					label: t("quick_active"),
					filters: [{ fieldId: "status", operator: "eq", value: "active" }],
				},
				{
					id: "low_quality",
					label: t("quick_low_quality"),
					filters: [{ fieldId: "quality_score", operator: "lt", value: "50" }],
				},
				{
					id: "high_quality",
					label: t("quick_high_quality"),
					filters: [{ fieldId: "quality_score", operator: "gte", value: "80" }],
				},
			],
			searchableFields: ["title", "description"],
		}),
		[categoryNodes],
	);

	// Initialize filters hook
	const filters = useFilters({
		schema: filterSchema,
		syncWithUrl: true,
	});

	// Back-compat: accept the legacy `?category=<id>` URL form
	// (used by deep-links from older callers / bookmarks) and
	// translate it to a proper `category_id` filter once.
	const legacyCatRef = useRef(false);
	useEffect(() => {
		if (legacyCatRef.current) return;
		const params = new URLSearchParams(window.location.search);
		const cat = params.get("category");
		if (!cat) return;
		legacyCatRef.current = true;
		const already = filters.state.filters.some(
			(f) => f.fieldId === "category_id" && f.value === cat,
		);
		if (!already) filters.addFilter("category_id", "eq", cat);
		params.delete("category");
		const url = new URL(window.location.href);
		url.search = params.toString();
		window.history.replaceState({}, "", url.toString());
	}, [filters]);

	// Page token map: AG Grid startRow → server page token
	const pageTokenMap = useRef<Map<number, string>>(new Map());
	const [totalProducts, setTotalProducts] = useState(0);
	const [initialLoading, setInitialLoading] = useState(true);

	/** Map from AG Grid colId to backend filter field name */
	const colToField: Record<string, string> = useMemo(
		() => ({
			title: "title",
			brand: "brand",
			vendor: "vendor_code",
			category: "category_id",
			price: "price",
			status: "status",
			quality: "quality_score",
			created: "created_at",
		}),
		[],
	);

	const datasource = useMemo<IDatasource>(
		() => ({
			getRows: async (params: IGetRowsParams) => {
				try {
					const pageToken = pageTokenMap.current.get(params.startRow) ?? "";
					const sortCol = params.sortModel[0];

					// Merge useFilters protobuf with AG Grid column filters
					const barFilter = filters.toProtobuf();
					const gridFilters = agFilterModelToProto(
						params.filterModel,
						colToField,
					);
					let filter: FilterGroup | undefined;
					if (barFilter && gridFilters.length > 0) {
						filter = new FilterGroup({
							logic: FilterGroup_Logic.AND,
							filters: [...barFilter.filters, ...gridFilters],
						});
					} else if (barFilter) {
						filter = barFilter;
					} else if (gridFilters.length > 0) {
						filter = new FilterGroup({
							logic: FilterGroup_Logic.AND,
							filters: gridFilters,
						});
					}

					const resp = await client.listProducts({
						page: { pageSize: PAGE_SIZE, pageToken },
						search: filters.debouncedSearch,
						filter,
						sort: sortCol
							? { field: sortCol.colId, descending: sortCol.sort === "desc" }
							: undefined,
						excludeVariants: true,
						ownerPartyId: ownerPartyId ?? "",
					});
					const rows = resp.products;
					const total = resp.page?.totalCount ?? rows.length;
					setTotalProducts(total);

					// Store next page token for the next block
					if (resp.page?.nextPageToken) {
						pageTokenMap.current.set(
							params.startRow + rows.length,
							resp.page.nextPageToken,
						);
					}

					// Resolve media thumbnails for this block
					const mediaIds = [...new Set(rows.flatMap((p) => p.mediaIds))];
					if (mediaIds.length > 0) {
						try {
							const urls = await mediaClient.batchGetMediaUrls({ mediaIds });
							for (const u of urls.urls) {
								thumbMapRef.current.set(u.mediaId, u.url);
							}
						} catch {
							/* thumbnails are best-effort */
						}
					}

					const lastRow =
						rows.length < PAGE_SIZE ? params.startRow + rows.length : -1;
					params.successCallback(rows, lastRow);
					setInitialLoading(false);
				} catch {
					params.failCallback();
				}
			},
		}),
		[filters.debouncedSearch, filters.state, ownerPartyId],
	);

	// Reset grid when datasource changes (filters/search changed) — skip initial mount
	const dsInitRef = useRef(true);
	useEffect(() => {
		if (dsInitRef.current) {
			dsInitRef.current = false;
			return;
		}
		pageTokenMap.current.clear();
		gridRef.current?.api?.setGridOption("datasource", datasource);
	}, [datasource]);

	const createMut = useMutation({
		mutationFn: (vals: {
			title: string;
			description: string;
			vendorCode?: string;
			priceCents?: number;
			categoryId?: string;
			isDraft: boolean;
		}) =>
			client.createProduct({
				title: vals.title,
				description: vals.description,
				isDraft: vals.isDraft,
				vendorCodes: vals.vendorCode
					? [
							{
								code: vals.vendorCode,
								name: vals.vendorCode,
								isDefault: true,
							},
						]
					: [],
				pricing:
					vals.priceCents != null && vals.priceCents > 0
						? [
								{
									name: t("default_price_name"),
									valueCents: BigInt(vals.priceCents),
									currency: "RUB",
									isDefault: true,
								},
							]
						: [],
				categoryIds: vals.categoryId ? [vals.categoryId] : [],
			}),
		onSuccess: (product) => {
			refreshGrid();
			qc.invalidateQueries({ queryKey: ["home", "products"] });
			qc.invalidateQueries({ queryKey: ["stats", "products"] });
			setDialogOpen(false);
			toast.success(t("create_success"));
			navigate({ to: "/catalog/products/$id", params: { id: product.id } });
		},
		onError: () => toast.error(t("create_error")),
	});

	const bulkDeleteMut = useMutation({
		mutationFn: (ids: string[]) =>
			client.bulkUpdateProducts({
				ids,
				delete: true,
			}),
		onSuccess: () => {
			refreshGrid();
			qc.invalidateQueries({ queryKey: ["home", "products"] });
			qc.invalidateQueries({ queryKey: ["stats", "products"] });
			clearSelection();
			gridRef.current?.api?.deselectAll();
			toast.success(t("bulk_delete_success"));
		},
		onError: () => toast.error(t("bulk_delete_error")),
	});

	const bulkDraftMut = useMutation({
		mutationFn: ({
			ids,
			isDraft,
		}: {
			ids: string[];
			isDraft: boolean;
		}) =>
			client.bulkUpdateProducts({
				ids,
				isDraft,
			}),
		onSuccess: () => {
			refreshGrid();
			qc.invalidateQueries({ queryKey: ["home", "products"] });
			clearSelection();
			gridRef.current?.api?.deselectAll();
			toast.success(t("bulk_status_success"));
		},
		onError: () => toast.error(t("bulk_status_error")),
	});

	const {
		selectedRows,
		hasSelection,
		onSelectionChanged,
		clearSelection,
		ActionBar,
	} = useGridSelection<Product>({
		onDelete: (rows) => bulkDeleteMut.mutate(rows.map((r) => r.id)),
		isPending: bulkDeleteMut.isPending || bulkDraftMut.isPending,
		entityLabel: t("entity_label", { defaultValue: "товаров" }),
		total: totalProducts,
		actions: [
			{
				id: "draft",
				label: t("set_draft"),
				onClick: (rows) =>
					bulkDraftMut.mutate({
						ids: (rows as Product[]).map((r) => r.id),
						isDraft: true,
					}),
			},
			{
				id: "active",
				label: t("set_active"),
				onClick: (rows) =>
					bulkDraftMut.mutate({
						ids: (rows as Product[]).map((r) => r.id),
						isDraft: false,
					}),
			},
		],
	});

	const selectedCount = selectedRows.length;

	// Thumbnail cache — populated incrementally by datasource getRows
	const thumbMapRef = useRef(new Map<string, string>());
	const thumbMap = thumbMapRef.current;

	// ── AG Grid column definitions ──
	const columnDefs = useMemo<ColDef<Product>[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				colId: "thumb",
				headerName: "",
				width: 48,
				maxWidth: 48,
				sortable: false,
				filter: false,
				resizable: false,
				cellRenderer: (p: { data: Product | undefined }) => {
					const firstMedia = p.data?.mediaIds[0];
					const url = firstMedia ? thumbMap.get(firstMedia) : undefined;
					return url ? (
						<img
							src={url}
							alt=""
							style={{
								height: 24,
								width: 24,
								borderRadius: 4,
								objectFit: "cover",
							}}
						/>
					) : (
						<div
							style={{
								height: 24,
								width: 24,
								borderRadius: 4,
								background: "hsl(var(--muted))",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<svg
								width="14"
								height="14"
								viewBox="0 0 24 24"
								fill="none"
								stroke="hsl(var(--muted-foreground))"
								strokeWidth="2"
							>
								<rect x="3" y="3" width="18" height="18" rx="2" />
								<circle cx="9" cy="9" r="2" />
								<path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
							</svg>
						</div>
					);
				},
			},
			{
				field: "title",
				headerName: t("col_title"),
				flex: 2,
				minWidth: 200,
				cellRenderer: (p: { data: Product | undefined }) => {
					if (!p.data) return "";
					return (
						<div>
							<div
								style={{
									fontWeight: 500,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									maxWidth: 360,
								}}
							>
								{p.data.title}
								{p.data.variantCount > 0 && (
									<span
										style={{
											fontSize: 10,
											color: "hsl(var(--muted-foreground))",
											background: "hsl(var(--muted))",
											padding: "1px 6px",
											borderRadius: 9,
											marginLeft: 4,
										}}
									>
										{p.data.variantCount} {t("variant_count_short")}
									</span>
								)}
							</div>
							{p.data.description && (
								<div
									style={{
										fontSize: 11,
										color: "hsl(var(--muted-foreground))",
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
										maxWidth: 360,
									}}
								>
									{p.data.description}
								</div>
							)}
						</div>
					);
				},
			},
			{
				colId: "brand",
				headerName: t("col_brand", { defaultValue: "Бренд" }),
				width: 120,
				hide: true,
				valueGetter: (p) => (p.data as any)?.brand ?? "",
			},
			{
				colId: "vendor",
				headerName: t("col_vendor_code"),
				width: 120,
				cellClass: "font-mono",
				valueGetter: (p) => (p.data ? formatVendor(p.data) : ""),
			},
			{
				colId: "category",
				headerName: t("col_category"),
				width: 150,
				filter: false,
				valueGetter: (p) =>
					(p.data?.categoryIds ?? [])
						.map((cid) => catMap.get(cid)?.name)
						.filter(Boolean)
						.join(", ") || "—",
				cellRenderer: (p: { data: Product | undefined }) => {
					const cats = (p.data?.categoryIds ?? [])
						.map((cid) => {
							const c = catMap.get(cid);
							return c ? { id: cid, name: c.name } : null;
						})
						.filter((c): c is { id: string; name: string } => !!c);
					if (cats.length === 0) return "—";
					const active = filters.state.filters.find(
						(f) => f.fieldId === "category_id",
					);
					return (
						<div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
							{cats.map((c) => {
								const isActive = active?.value === c.id;
								return (
									<button
										key={c.id}
										type="button"
										title={t("filter_by_category", {
											defaultValue: "Фильтровать по категории",
										})}
										onClick={(e) => {
											e.stopPropagation();
											if (active) filters.removeFilter(active.id);
											if (!isActive)
												filters.addFilter("category_id", "eq", c.id);
										}}
										style={{
											fontSize: 11,
											lineHeight: "16px",
											padding: "1px 6px",
											borderRadius: 4,
											border: "1px solid hsl(var(--border))",
											background: isActive
												? "hsl(var(--accent))"
												: "transparent",
											color: "inherit",
											cursor: "pointer",
											maxWidth: 140,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									>
										{c.name}
									</button>
								);
							})}
						</div>
					);
				},
			},
			{
				colId: "price",
				headerName: t("col_price"),
				width: 110,
				type: "rightAligned",
				filter: "agNumberColumnFilter",
				cellClass: "font-tabular",
				valueGetter: (p) => {
					const def =
						p.data?.pricing.find((pr) => pr.isDefault) ?? p.data?.pricing[0];
					return def ? Number(def.valueCents) / 100 : 0;
				},
				valueFormatter: (p) => (p.value && p.data ? formatPrice(p.data) : "—"),
			},
			{
				colId: "supplier_cost",
				headerName: t("col_supplier_cost"),
				width: 110,
				type: "rightAligned",
				sortable: false,
				filter: false,
				cellClass: "font-tabular",
				valueGetter: (p) =>
					p.data ? Number(p.data.supplierCostCents) / 100 : 0,
				valueFormatter: (p) =>
					p.value && p.data ? formatSupplierCost(p.data) : "—",
			},
			{
				colId: "sell",
				headerName: t("col_sell"),
				width: 110,
				type: "rightAligned",
				sortable: false,
				filter: false,
				cellClass: "font-tabular",
				valueGetter: (p) => {
					const sell = p.data?.pricing.find((pr) => pr.name === "SELL");
					return sell ? Number(sell.valueCents) / 100 : null;
				},
				valueFormatter: (p) => {
					if (p.value == null || !p.data) return "—";
					const sell = p.data.pricing.find((pr) => pr.name === "SELL");
					return `${p.value.toFixed(2)} ${sell?.currency || "₽"}`;
				},
			},
			{
				colId: "margin",
				headerName: t("col_margin"),
				width: 90,
				type: "rightAligned",
				sortable: false,
				filter: false,
				cellClass: "font-tabular",
				valueGetter: (p) => (p.data ? computeMarginPct(p.data) : null),
				valueFormatter: (p) => (p.value == null ? "—" : `${p.value}%`),
			},
			{
				colId: "status",
				headerName: t("col_status"),
				width: 90,
				filter: false,
				cellRenderer: (p: { data: Product | undefined }) => {
					if (!p.data?.isDraft) return "";
					return (
						<span
							style={{
								display: "inline-block",
								padding: "1px 6px",
								border: "1px solid hsl(var(--border))",
								borderRadius: 4,
								fontSize: 11,
								lineHeight: "18px",
							}}
						>
							{t("col_draft")}
						</span>
					);
				},
			},
			{
				colId: "quality",
				headerName: t("col_quality"),
				width: 80,
				hide: true,
				filter: "agNumberColumnFilter",
				valueGetter: (p) => p.data?.qualityScore ?? 0,
				cellRenderer: (p: { value: number }) => {
					if (!p.value) return "";
					const color =
						p.value >= 80
							? "hsl(var(--chart-2))"
							: p.value >= 50
								? "#d97706"
								: "hsl(var(--destructive))";
					return (
						<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
							<div
								style={{
									width: 40,
									height: 6,
									background: "hsl(var(--muted))",
									borderRadius: 3,
									overflow: "hidden",
								}}
							>
								<div
									style={{
										height: "100%",
										borderRadius: 3,
										background: color,
										width: `${p.value}%`,
									}}
								/>
							</div>
							<span
								style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}
							>
								{p.value}
							</span>
						</div>
					);
				},
			},
			{
				colId: "created",
				headerName: t("col_created", { defaultValue: "Создан" }),
				width: 100,
				hide: true,
				filter: "agDateColumnFilter",
				valueGetter: (p) => p.data?.createdAt?.toDate(),
				valueFormatter: (p) => {
					if (!p.value) return "";
					return new Date(p.value).toLocaleDateString("ru-RU");
				},
			},
		],
		[t, catMap, thumbMap, filters],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
			filter: "agTextColumnFilter",
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	);

	const onRowClicked = useCallback(
		(e: { data: Product | undefined }) => {
			if (e.data) {
				navigate({ to: "/catalog/products/$id", params: { id: e.data.id } });
			}
		},
		[navigate],
	);

	// ── Status bar ──
	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: hasSelection
					? `${selectedCount} ${t("n_selected", { count: selectedCount, defaultValue: "выбрано" })}`
					: `${totalProducts} ${t("metric_total_products_short", { defaultValue: "товаров" })}`,
				align: "left" as const,
			},
		],
		[hasSelection, selectedCount, totalProducts, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<>
			{ownerPartyId && (
				<div className="flex items-center gap-2 px-4 py-2 border-b bg-muted/30 text-sm">
					<span className="text-muted-foreground">Фильтр:</span>
					<span>
						товары поставщика{" "}
						<code className="font-mono text-xs">
							{ownerPartyId.slice(0, 8)}
						</code>
					</span>
					<Button
						variant="ghost"
						size="sm"
						className="h-6 px-2"
						onClick={() =>
							navigate({
								to: "/catalog/products",
								search: { owner_party_id: undefined },
							})
						}
					>
						очистить ×
					</Button>
				</div>
			)}
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("filter_title", {
							defaultValue: "Поиск товаров...",
						})}
						value={filters.searchInput}
						onChange={(e) => filters.setSearch(e.target.value)}
						className="pl-9 h-9"
					/>
				</div>
				<Select
					value={
						(filters.state.filters.find((f) => f.fieldId === "status")
							?.value as string) ?? "all"
					}
					onValueChange={(v) => {
						filters.clearFilters();
						if (v !== "all") {
							filters.addFilter("status", "eq", v);
						}
					}}
				>
					<SelectTrigger className="w-[160px] h-9">
						<SelectValue placeholder={t("col_status")} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">
							{t("filter_all", { defaultValue: "Все статусы" })}
						</SelectItem>
						<SelectItem value="draft">{t("filter_status_draft")}</SelectItem>
						<SelectItem value="active">{t("filter_status_active")}</SelectItem>
					</SelectContent>
				</Select>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => setImportDialogOpen(true)}
				>
					{t("import")}
				</Button>
				<Button size="sm" className="h-9" onClick={() => setDialogOpen(true)}>
					<Plus className="h-4 w-4 mr-1" />
					{t("new_product")}
				</Button>
			</div>

			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("new_product")}</DialogTitle>
					</DialogHeader>
					<CreateProductForm
						categories={categoriesWithReadablePath}
						onSubmit={(v) => createMut.mutate(v)}
						isPending={createMut.isPending}
						error={createMut.isError ? createMut.error?.message : undefined}
					/>
				</DialogContent>
			</Dialog>

			<ImportDialog
				open={importDialogOpen}
				onOpenChange={setImportDialogOpen}
			/>

			{initialLoading && <GridSpinner />}
			<div
				className="flex-1 ag-theme-quartz h-full"
				style={{ width: "100%", display: initialLoading ? "none" : undefined }}
			>
				<AgGridReact<Product>
					ref={gridRef}
					rowModelType="infinite"
					datasource={datasource}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowSelection="multiple"
					onSelectionChanged={onSelectionChanged}
					onRowClicked={onRowClicked}
					onGridReady={onGridReady}
					onSortChanged={() => {
						onStateChanged();
						refreshGrid();
					}}
					onFilterChanged={() => {
						onStateChanged();
						refreshGrid();
					}}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					suppressRowClickSelection
					rowClass="cursor-pointer"
					animateRows={false}
					rowHeight={28}
					headerHeight={32}
					getRowId={(p) => p.data?.id ?? String(p.level ?? 0)}
					cacheBlockSize={PAGE_SIZE}
					maxBlocksInCache={20}
					loadingCellRenderer={LoadingCellRenderer}
					overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("empty_no_products_title")}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("empty_no_products_description")}</p></div>`}
					overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("loading")}...</span>`}
				/>
			</div>

			<ActionBar />
		</>
	);
}

interface TreeCatNode {
	id: string;
	name: string;
	readablePath: string;
	children: TreeCatNode[];
}

function buildCategoryTree(
	categories: {
		id: string;
		parentId: string;
		name: string;
		readablePath: string;
	}[],
): TreeCatNode[] {
	const byId = new Map<string, TreeCatNode>();
	const roots: TreeCatNode[] = [];

	for (const c of categories) {
		byId.set(c.id, {
			id: c.id,
			name: c.name,
			readablePath: c.readablePath,
			children: [],
		});
	}

	for (const c of categories) {
		const node = byId.get(c.id)!;
		if (c.parentId && byId.has(c.parentId)) {
			byId.get(c.parentId)!.children.push(node);
		} else {
			roots.push(node);
		}
	}

	return roots;
}

function CategoryTreeSelect({
	categories,
	search,
	onSearchChange,
	onSelect,
}: {
	categories: {
		id: string;
		parentId: string;
		name: string;
		readablePath: string;
	}[];
	search: string;
	onSearchChange: (v: string) => void;
	onSelect: (id: string) => void;
}) {
	const { t } = useTranslation("products");
	const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

	const tree = useMemo(() => buildCategoryTree(categories), [categories]);

	const filtered = useMemo(() => {
		if (!search.trim()) return tree;
		const lower = search.toLowerCase();
		const matching = categories.filter(
			(c) =>
				c.name.toLowerCase().includes(lower) ||
				c.readablePath.toLowerCase().includes(lower),
		);
		return matching.map((c) => ({
			id: c.id,
			name: c.name,
			readablePath: c.readablePath,
			children: [] as TreeCatNode[],
		}));
	}, [tree, categories, search]);

	const toggleExpand = useCallback((id: string) => {
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const renderNode = (node: TreeCatNode, depth = 0) => {
		const hasChildren = node.children.length > 0;
		const isExpanded = expanded.has(node.id);

		return (
			<div key={node.id}>
				<div
					className={cn(
						"flex items-center gap-1 py-1 px-2 rounded cursor-pointer hover:bg-muted text-sm",
					)}
					style={{ paddingLeft: `${depth * 16 + 8}px` }}
				>
					{hasChildren ? (
						<Button
							variant="ghost"
							size="icon"
							className="h-5 w-5 p-0.5"
							onClick={(e) => {
								e.stopPropagation();
								toggleExpand(node.id);
							}}
						>
							{isExpanded ? (
								<ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
							) : (
								<ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
							)}
						</Button>
					) : (
						<span className="w-5" />
					)}
					{hasChildren ? (
						isExpanded ? (
							<FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
						) : (
							<Folder className="h-4 w-4 text-muted-foreground shrink-0" />
						)
					) : (
						<span className="w-4" />
					)}
					<span className="truncate flex-1" onClick={() => onSelect(node.id)}>
						{node.name}
					</span>
				</div>
				{hasChildren && isExpanded && (
					<div>
						{node.children.map((child) => renderNode(child, depth + 1))}
					</div>
				)}
			</div>
		);
	};

	return (
		<div className="space-y-2">
			<div className="relative">
				<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
				<Input
					value={search}
					onChange={(e) => onSearchChange(e.target.value)}
					placeholder={t("search_category")}
					className="h-8 pl-8 text-sm"
				/>
			</div>
			<div className="max-h-64 overflow-y-auto">
				{filtered.length === 0 ? (
					<p className="text-sm text-muted-foreground text-center py-4">
						{t("no_categories_found")}
					</p>
				) : (
					filtered.map((node) => renderNode(node))
				)}
			</div>
		</div>
	);
}

function CreateProductForm({
	categories,
	onSubmit,
	isPending,
	error,
}: {
	categories: {
		id: string;
		parentId: string;
		name: string;
		readablePath: string;
	}[];
	onSubmit: (v: {
		title: string;
		description: string;
		vendorCode?: string;
		priceCents?: number;
		categoryId?: string;
		isDraft: boolean;
	}) => void;
	isPending: boolean;
	error?: string;
}) {
	const { t } = useTranslation("products");
	const [title, setTitle] = useState("");
	const [desc, setDesc] = useState("");
	const [vendorCode, setVendorCode] = useState("");
	const [priceRub, setPriceRub] = useState("");
	const [categoryId, setCategoryId] = useState("");
	const [isDraft, setIsDraft] = useState(false);
	const [catPopoverOpen, setCatPopoverOpen] = useState(false);
	const [catSearch, setCatSearch] = useState("");

	const selectedCat = useMemo(
		() => categories.find((c) => c.id === categoryId),
		[categories, categoryId],
	);

	return (
		<div className="space-y-3">
			<Input
				placeholder={t("create_product_title_placeholder")}
				value={title}
				onChange={(e) => setTitle(e.target.value)}
			/>
			<Textarea
				placeholder={t("create_description_placeholder")}
				value={desc}
				onChange={(e) => setDesc(e.target.value)}
				rows={2}
			/>
			<div className="grid grid-cols-2 gap-3">
				<Input
					placeholder={t("create_vendor_code_placeholder")}
					value={vendorCode}
					onChange={(e) => setVendorCode(e.target.value)}
					className="font-mono"
				/>
				<Input
					placeholder={t("create_price_placeholder")}
					value={priceRub}
					onChange={(e) => setPriceRub(e.target.value)}
					type="number"
					step="0.01"
				/>
			</div>

			<Popover open={catPopoverOpen} onOpenChange={setCatPopoverOpen}>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						className="w-full justify-between font-normal"
					>
						{selectedCat
							? selectedCat.readablePath
							: t("create_category_placeholder")}
						<FolderTree className="h-4 w-4 text-muted-foreground" />
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-80 p-3" align="start">
					<CategoryTreeSelect
						categories={categories}
						search={catSearch}
						onSearchChange={setCatSearch}
						onSelect={(id) => {
							setCategoryId(id);
							setCatPopoverOpen(false);
							setCatSearch("");
						}}
					/>
				</PopoverContent>
			</Popover>

			<Label className="flex items-center gap-2 text-muted-foreground cursor-pointer">
				<Checkbox
					checked={isDraft}
					onCheckedChange={(v) => setIsDraft(v === true)}
				/>
				{t("create_draft_label")}
			</Label>

			{error && <p className="text-sm text-destructive">{error}</p>}

			<Button
				className="w-full"
				size="sm"
				disabled={!title || isPending}
				onClick={() => {
					const cents = priceRub
						? Math.round(Number.parseFloat(priceRub) * 100)
						: undefined;
					onSubmit({
						title,
						description: desc,
						vendorCode: vendorCode || undefined,
						priceCents: cents,
						categoryId: categoryId || undefined,
						isDraft,
					});
				}}
			>
				{isPending ? t("creating") : t("common:create")}
			</Button>
		</div>
	);
}
